import React, { useState, useEffect } from 'react';
import { ArrowRight, CreditCard, Building2, Wallet, RefreshCw, Check, ArrowUpDown, ChevronDown, ScanFace, Lock, Plus, Minus } from 'lucide-react';
import logo from '@/assets/logo.png';
import { CryptoAsset, FiatCurrency } from '../types/crypto';

// Fallback rates in case API fails
const FALLBACK_RATES = {
  [CryptoAsset.BTC]: 64000.00,
  [CryptoAsset.ETH]: 3400.00,
  [CryptoAsset.USDC]: 1.00,
  [CryptoAsset.SOL]: 145.00,
  [CryptoAsset.MATIC]: 0.50
};

const COINGECKO_IDS = {
  [CryptoAsset.BTC]: 'bitcoin',
  [CryptoAsset.ETH]: 'ethereum',
  [CryptoAsset.USDC]: 'usd-coin',
  [CryptoAsset.SOL]: 'solana',
  [CryptoAsset.MATIC]: 'matic-network'
};

const FIAT_CONVERSION = {
  [FiatCurrency.USD]: 1,
  [FiatCurrency.EUR]: 0.92, 
  [FiatCurrency.GBP]: 0.78, 
  [FiatCurrency.CAD]: 1.36  
};

// Extended enum for internal flow
enum InternalWidgetStep {
  AMOUNT = 'AMOUNT',
  ADDRESS = 'ADDRESS',       // Buy: Input wallet
  BANK = 'BANK',             // Sell: Input bank details
  EMAIL = 'EMAIL',
  VERIFICATION = 'VERIFICATION',
  PAYMENT = 'PAYMENT',       // Buy: Pay with card
  DEPOSIT = 'DEPOSIT',       // Sell: Send crypto to NowRamp
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS'
}

const CryptoIcon = ({ asset, className = "w-6 h-6" }: { asset: CryptoAsset; className?: string }) => {
  switch (asset) {
    case CryptoAsset.BTC:
      return (
        <svg viewBox="0 0 32 32" className={className}>
          <circle cx="16" cy="16" r="16" fill="#F7931A" />
          <path fill="#FFF" d="M23.848 12.898c.312-2.08-1.272-3.196-3.434-3.938l.702-2.813-1.71-.427-.683 2.736c-.45-.112-.912-.215-1.382-.318l.686-2.747-1.713-.428-.7 2.807c-.375-.085-.745-.167-1.104-.25l.002-.008-2.36.59.456 1.828s1.27-.31 1.246-.294c.695.174.82.635.8.998l-.802 3.215c.048.012.11.03.18.057l-.183-.045-1.124 4.5c-.086.212-.302.53-.792.41.025.034-1.247-.312-1.247-.312l-.85 1.96 2.225.556c.415.104.823.212 1.223.313l-.71 2.847 1.71.427.705-2.823c.467.126.92.24 1.365.343l-.7 2.807 1.712.427.71-2.85c2.917.553 5.112.33 6.036-2.31.744-2.126-.037-3.35-1.574-4.15 1.118-.258 1.96-.993 2.184-2.514z" />
        </svg>
      );
    case CryptoAsset.ETH:
      return (
        <svg viewBox="0 0 32 32" className={className}>
          <circle cx="16" cy="16" r="16" fill="#627EEA" />
          <path fill="#FFF" fillOpacity="0.602" d="M16.498 4v8.87l7.497 3.35zm0 0L9 16.22l7.498-3.35zm0 12.73l7.497-4.354L16.5 25.23zm0 0L9 16.22l7.498 4.354z" />
          <path fill="#FFF" d="M16.498 25.23V28l7.497-10.39L16.5 16.22zM9 17.61L16.498 28v-2.77z" />
          <path fill="#FFF" fillOpacity="0.45" d="M16.498 16.221V4L9 16.22z" />
        </svg>
      );
    case CryptoAsset.USDC:
      return (
        <svg viewBox="0 0 32 32" className={className}>
          <circle cx="16" cy="16" r="16" fill="#2775CA" />
          <path fill="#FFF" d="M16 23a7 7 0 1 1 0-14 7 7 0 0 1 0 14zm-2.5-9.5a1.5 1.5 0 1 0 0 3h2v1.5h-3v1h3a2.5 2.5 0 1 0 0-5h-2v-1.5h3v-1h-3z" />
        </svg>
      );
    case CryptoAsset.SOL:
       return (
        <svg viewBox="0 0 32 32" className={className}>
            <defs>
                <linearGradient id="solGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#9945FF" />
                    <stop offset="100%" stopColor="#14F195" />
                </linearGradient>
            </defs>
            <circle cx="16" cy="16" r="16" fill="#000" />
            <path d="M8 13.5h12.5l-2 3.5H6zM10 8h12.5l-2 3.5H8zM8 19h12.5l-2 3.5H6z" fill="url(#solGrad)" />
        </svg>
       );
    case CryptoAsset.MATIC:
        return (
            <svg viewBox="0 0 32 32" className={className}>
                <circle cx="16" cy="16" r="16" fill="#8247E5" />
                <path d="M16 12l-3.5 2v4l3.5-2 3.5 2v-4z" fill="white" />
                <path d="M8 12l3.5 2v4L8 16zM24 12l-3.5 2v4l3.5-2z" fill="white" opacity="0.7"/>
            </svg>
        );
  }
  return <div className={`${className} bg-slate-500 rounded-full`} />;
};

const FiatFlag = ({ currency, className = "w-6 h-6" }: { currency: FiatCurrency; className?: string }) => {
  switch (currency) {
    case FiatCurrency.USD:
      return (
        <svg viewBox="0 0 32 32" className={`${className} rounded-full`}>
          <circle cx="16" cy="16" r="16" fill="#eee" />
          <g clipPath="circle(16px at 16px 16px)">
             <rect y="4" width="32" height="3" fill="#B22234" />
             <rect y="10" width="32" height="3" fill="#B22234" />
             <rect y="16" width="32" height="3" fill="#B22234" />
             <rect y="22" width="32" height="3" fill="#B22234" />
             <rect width="16" height="16" fill="#3C3B6E" />
          </g>
        </svg>
      );
    case FiatCurrency.EUR:
      return (
        <svg viewBox="0 0 32 32" className={`${className} rounded-full`}>
          <circle cx="16" cy="16" r="16" fill="#003399" />
          <path d="M16 5v2M16 25v2M5 16h2M25 16h2" stroke="#FFCC00" strokeWidth="2" strokeLinecap="round" />
          <path d="M8.2 8.2l1.5 1.5M22.3 22.3l1.5 1.5M8.2 23.8l1.5-1.5M22.3 9.7l1.5-1.5" stroke="#FFCC00" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case FiatCurrency.GBP:
        return (
            <svg viewBox="0 0 32 32" className={`${className} rounded-full`}>
                <circle cx="16" cy="16" r="16" fill="#012169" />
                <path d="M0 0l32 32m0-32l-32 32" stroke="white" strokeWidth="4" />
                <path d="M0 0l32 32m0-32l-32 32" stroke="#C8102E" strokeWidth="2" />
                <path d="M16 0v32M0 16h32" stroke="white" strokeWidth="6" />
                <path d="M16 0v32M0 16h32" stroke="#C8102E" strokeWidth="4" />
            </svg>
        );
    case FiatCurrency.CAD:
         return (
            <svg viewBox="0 0 32 32" className={`${className} rounded-full`}>
                <circle cx="16" cy="16" r="16" fill="#eee" />
                <path d="M0 0h10v32H0zM22 0h10v32H22z" fill="#FF0000" clipPath="circle(16px at 16px 16px)"/>
                <path d="M16 4l5 12h-2l2 8h-5v4h-2v-4h-5l2-8H9z" fill="#FF0000" />
            </svg>
         );
  }
  return <div className={`${className} bg-slate-500 rounded-full`} />;
};

const Widget: React.FC = () => {
  const [mode, setMode] = useState<'buy' | 'sell'>('buy');
  const [step, setStep] = useState<InternalWidgetStep>(InternalWidgetStep.AMOUNT);
  
  const [amount, setAmount] = useState<string>('1000');
  const [fiatCurrency, setFiatCurrency] = useState<FiatCurrency>(FiatCurrency.USD);
  const [cryptoAsset, setCryptoAsset] = useState<CryptoAsset>(CryptoAsset.ETH);
  
  const [walletAddress, setWalletAddress] = useState('');
  const [bankAccount, setBankAccount] = useState({ name: '', number: '' });
  const [email, setEmail] = useState('');

  const [estimatedOutput, setEstimatedOutput] = useState<string>('0');
  const [loadingRate, setLoadingRate] = useState(false);
  const [isAmountFocused, setIsAmountFocused] = useState(false);
  
  const [liveRates, setLiveRates] = useState(FALLBACK_RATES);
  const [exchangeRate, setExchangeRate] = useState<number>(0);

  // Fetch live rates from CoinGecko
  const fetchRates = async () => {
    try {
      const ids = Object.values(COINGECKO_IDS).join(',');
      const response = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd`);
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      
      const newRates = { ...FALLBACK_RATES };
      
      Object.entries(COINGECKO_IDS).forEach(([asset, id]) => {
        if (data[id]?.usd) {
          newRates[asset as CryptoAsset] = data[id].usd;
        }
      });

      setLiveRates(newRates);
    } catch (error) {
      console.error("Failed to fetch live rates:", error);
      // Keep using current/fallback rates silently
    }
  };

  // Real-time rate fetching (Every 1 minute)
  useEffect(() => {
    fetchRates(); // Initial fetch
    const interval = setInterval(fetchRates, 60000); // 60 seconds
    return () => clearInterval(interval);
  }, []);

  // Calculate Output based on inputs and live rates
  useEffect(() => {
    setLoadingRate(true);
    const timer = setTimeout(() => {
      const baseCryptoPriceInUSD = liveRates[cryptoAsset];
      const fiatConversionRate = FIAT_CONVERSION[fiatCurrency];
      
      // Exchange Rate: How much Fiat for 1 unit of Crypto
      const safeCryptoPrice = baseCryptoPriceInUSD || FALLBACK_RATES[cryptoAsset];
      const currentRate = safeCryptoPrice * fiatConversionRate;
      
      setExchangeRate(currentRate);

      const val = parseFloat(amount) || 0;
      
      if (mode === 'buy') {
          // Input is Fiat, Output is Crypto
          const fees = val * 0.02; // 2% fee
          const netFiat = val - fees;
          // Prevent division by zero
          const cryptoOut = currentRate > 0 ? netFiat / currentRate : 0;
          setEstimatedOutput(cryptoOut.toFixed(cryptoAsset === CryptoAsset.USDC ? 2 : 6));
      } else {
          // Input is Crypto, Output is Fiat
          const grossFiat = val * currentRate;
          const fees = grossFiat * 0.02; // 2% fee
          const netFiat = grossFiat - fees;
          setEstimatedOutput(netFiat.toFixed(2));
      }
      setLoadingRate(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [amount, cryptoAsset, fiatCurrency, liveRates, mode]);

  const handleNext = () => {
    switch (step) {
        case InternalWidgetStep.AMOUNT:
            if (mode === 'buy') setStep(InternalWidgetStep.ADDRESS);
            else setStep(InternalWidgetStep.BANK);
            break;
        case InternalWidgetStep.ADDRESS:
             if (!walletAddress) return; 
             setStep(InternalWidgetStep.EMAIL);
             break;
        case InternalWidgetStep.BANK:
             if (!bankAccount.number) return;
             setStep(InternalWidgetStep.EMAIL);
             break;
        case InternalWidgetStep.EMAIL:
             if (!email) return;
             setStep(InternalWidgetStep.VERIFICATION);
             break;
        case InternalWidgetStep.VERIFICATION:
             if (mode === 'buy') setStep(InternalWidgetStep.PAYMENT);
             else setStep(InternalWidgetStep.DEPOSIT);
             break;
        case InternalWidgetStep.PAYMENT:
        case InternalWidgetStep.DEPOSIT:
             setStep(InternalWidgetStep.PROCESSING);
             setTimeout(() => setStep(InternalWidgetStep.SUCCESS), 4000);
             break;
        case InternalWidgetStep.SUCCESS:
             setStep(InternalWidgetStep.AMOUNT);
             setAmount('1000');
             setWalletAddress('');
             break;
    }
  };

  // Helper for currency formatting
  const formatFiat = (val: number) => {
      return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: fiatCurrency,
          minimumFractionDigits: 2
      }).format(val);
  };

  const renderAmountStep = () => (
    <div className="space-y-1 animate-in fade-in slide-in-from-right-4 duration-300">
      
      {/* Input Field (Source) */}
      <div className="bg-[#0F0F0F] rounded-[20px] p-4 transition-all border border-transparent hover:border-white/5 focus-within:border-blue-500/30 focus-within:ring-1 focus-within:ring-blue-500/20 group relative overflow-hidden">
         <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block pl-1">
            {mode === 'buy' ? 'You Pay' : 'You Sell'}
        </label>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <input
              type="text"
              value={amount}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.]/g, '');
                setAmount(val);
              }}
              onFocus={() => setIsAmountFocused(true)}
              onBlur={() => setIsAmountFocused(false)}
              className="w-full bg-transparent text-4xl font-bold text-white outline-none placeholder-slate-700 tracking-tight"
              placeholder="0"
            />
            {isAmountFocused && (
              <div className="flex flex-col gap-1 animate-fade-in">
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setAmount(String(Math.max(0, parseFloat(amount || '0') + 10)))}
                  aria-label="Increase amount by 10"
                  className="bg-[#1A1A1A] hover:bg-blue-500/20 border border-white/5 hover:border-blue-500/30 p-1.5 rounded-lg transition-all active:scale-95 group/inc"
                >
                  <Plus size={14} className="text-slate-400 group-hover/inc:text-blue-400 transition-colors" strokeWidth={2.5} aria-hidden="true" />
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setAmount(String(Math.max(0, parseFloat(amount || '0') - 10)))}
                  aria-label="Decrease amount by 10"
                  className="bg-[#1A1A1A] hover:bg-blue-500/20 border border-white/5 hover:border-blue-500/30 p-1.5 rounded-lg transition-all active:scale-95 group/dec"
                >
                  <Minus size={14} className="text-slate-400 group-hover/dec:text-blue-400 transition-colors" strokeWidth={2.5} aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
           <div className="flex items-center gap-2 bg-[#1A1A1A] pl-2 pr-3 py-2 rounded-full hover:bg-[#252525] transition-colors cursor-pointer border border-white/5 group/select shrink-0 relative">
            {mode === 'buy' ? (
                <>
                    <FiatFlag currency={fiatCurrency} className="w-6 h-6 shadow-sm" />
                    <select 
                      value={fiatCurrency}
                      onChange={(e) => setFiatCurrency(e.target.value as FiatCurrency)}
                      aria-label="Select payment currency"
                      className="bg-transparent font-bold text-base text-white outline-none cursor-pointer appearance-none absolute inset-0 opacity-0 w-full h-full"
                    >
                      {Object.values(FiatCurrency).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <span className="text-base font-bold text-white pointer-events-none">{fiatCurrency}</span>
                </>
            ) : (
                <>
                     <CryptoIcon asset={cryptoAsset} className="w-6 h-6 shadow-sm" />
                     <select 
                      value={cryptoAsset}
                      onChange={(e) => setCryptoAsset(e.target.value as CryptoAsset)}
                      aria-label="Select cryptocurrency to sell"
                      className="bg-transparent font-bold text-base text-white outline-none cursor-pointer appearance-none absolute inset-0 opacity-0 w-full h-full"
                    >
                      {Object.values(CryptoAsset).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <span className="text-base font-bold text-white pointer-events-none">{cryptoAsset}</span>
                </>
            )}
            <ChevronDown size={14} className="text-slate-400 pointer-events-none group-hover/select:text-white transition-colors" />
          </div>
        </div>
      </div>

      {/* Divider / Swap Icon */}
      <div className="flex justify-center relative h-6 z-10">
         <div className="absolute top-1/2 -translate-y-1/2">
            <button 
                onClick={() => setMode(mode === 'buy' ? 'sell' : 'buy')}
                aria-label={mode === 'buy' ? 'Switch to sell mode' : 'Switch to buy mode'}
                className="bg-[#1A1A1A] border-2 border-[#0A0A0A] p-2 rounded-xl text-slate-400 cursor-pointer hover:text-blue-400 hover:border-blue-500/20 transition-all hover:scale-105 shadow-lg"
            >
                <ArrowUpDown size={14} strokeWidth={3} />
            </button>
         </div>
      </div>

      {/* Output Field (Destination) */}
      <div className="bg-[#0F0F0F] rounded-[20px] p-4 transition-all border border-transparent hover:border-white/5 group relative">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block pl-1">
             {mode === 'buy' ? 'You Receive' : 'You Receive'}
        </label>
        <div className="flex items-center justify-between gap-4">
          <div className="w-full text-4xl font-bold text-white tracking-tight overflow-hidden text-ellipsis">
            {loadingRate ? <RefreshCw className="animate-spin text-slate-700" size={32} /> : estimatedOutput}
          </div>
          
           <div className="flex items-center gap-2 bg-[#1A1A1A] pl-2 pr-3 py-2 rounded-full hover:bg-[#252525] transition-colors cursor-pointer border border-white/5 group/select shrink-0 relative overflow-hidden">
             {mode === 'buy' ? (
                <>
                    <CryptoIcon asset={cryptoAsset} className="w-6 h-6 shadow-sm" />
                    <select 
                        value={cryptoAsset}
                        onChange={(e) => setCryptoAsset(e.target.value as CryptoAsset)}
                        aria-label="Select cryptocurrency to receive"
                        className="bg-transparent font-bold text-base text-white outline-none cursor-pointer appearance-none absolute inset-0 opacity-0 w-full h-full"
                    >
                        {Object.values(CryptoAsset).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <span className="text-base font-bold text-white pointer-events-none">{cryptoAsset}</span>
                </>
             ) : (
                <>
                    <FiatFlag currency={fiatCurrency} className="w-6 h-6 shadow-sm" />
                    <select 
                        value={fiatCurrency}
                        onChange={(e) => setFiatCurrency(e.target.value as FiatCurrency)}
                        aria-label="Select currency to receive"
                        className="bg-transparent font-bold text-base text-white outline-none cursor-pointer appearance-none absolute inset-0 opacity-0 w-full h-full"
                    >
                        {Object.values(FiatCurrency).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <span className="text-base font-bold text-white pointer-events-none">{fiatCurrency}</span>
                </>
             )}
             <ChevronDown size={14} className="text-slate-400 pointer-events-none group-hover/select:text-white transition-colors" />
          </div>
        </div>
      </div>
      
      {/* Real-time Rate Display */}
      <div className="pt-4 pb-1">
        <div className="flex items-center justify-between px-3 py-2 bg-blue-500/5 border border-blue-500/10 rounded-lg">
            <div className="flex items-center gap-2">
                <div className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </div>
                <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wide">Live Rate (1M)</span>
            </div>
            <span className="text-xs font-mono text-blue-300">
                1 {cryptoAsset} ≈ {formatFiat(exchangeRate)}
            </span>
        </div>
      </div>

      <div className="bg-white/[0.02] rounded-xl p-4 space-y-3 border border-white/5 backdrop-blur-md">
        <div className="flex justify-between text-xs text-slate-400">
          <span>Processing Fee (2%)</span>
          <span className="text-slate-300">
              {mode === 'buy' 
                ? `${(parseFloat(amount || '0') * 0.02).toFixed(2)} ${fiatCurrency}` 
                : `${((parseFloat(amount || '0') * exchangeRate) * 0.02).toFixed(2)} ${fiatCurrency}`
              }
          </span>
        </div>
        <div className="flex justify-between text-xs text-slate-400">
          <span>Network Fee</span>
          <span className="text-slate-300">~2.50 {fiatCurrency}</span>
        </div>
         <div className="h-px bg-white/5 w-full my-1"></div>
         <div className="flex justify-between text-xs font-bold text-blue-400">
          <span>{mode === 'buy' ? 'Total Spend' : 'Total Receive'}</span>
          <span>
              {mode === 'buy' 
                  ? `${amount} ${fiatCurrency}`
                  : `${estimatedOutput} ${fiatCurrency}`
              }
          </span>
         </div>
      </div>

      <button 
        onClick={handleNext}
        className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-slate-200 transition-all flex items-center justify-center gap-2 mt-6 shadow-[0_0_20px_rgba(255,255,255,0.1)] active:scale-95"
      >
        Get Started <ArrowRight size={18} />
      </button>
    </div>
  );

  const renderAddressStep = () => (
    <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
        <h3 className="text-lg font-bold text-white">Enter Wallet Address</h3>
        <div className="bg-[#151515] rounded-xl p-4 border border-white/10 focus-within:border-blue-500 transition-colors">
            <label className="text-xs text-slate-500 block mb-2 uppercase font-bold tracking-wider">Destination {cryptoAsset}</label>
            <div className="flex items-center gap-3">
                <Wallet className="text-slate-400" size={20} />
                <input 
                    type="text" 
                    value={walletAddress}
                    onChange={(e) => setWalletAddress(e.target.value)}
                    placeholder={`Your ${cryptoAsset} Address`}
                    className="bg-transparent w-full text-white outline-none font-mono text-sm"
                />
            </div>
        </div>
        <button onClick={handleNext} disabled={!walletAddress} className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
            Continue
        </button>
        <button onClick={() => setStep(InternalWidgetStep.AMOUNT)} className="w-full text-slate-500 text-sm hover:text-white py-2">Back</button>
    </div>
  );

  const renderBankStep = () => (
    <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
        <h3 className="text-lg font-bold text-white">Bank Details</h3>
        <div className="space-y-3">
            <div className="bg-[#151515] rounded-xl p-4 border border-white/10">
                <label className="text-xs text-slate-500 block mb-2 uppercase font-bold tracking-wider">Account Name</label>
                <div className="flex items-center gap-3">
                    <ScanFace className="text-slate-400" size={20} />
                    <input 
                        type="text" 
                        value={bankAccount.name}
                        onChange={(e) => setBankAccount({...bankAccount, name: e.target.value})}
                        placeholder="John Doe"
                        className="bg-transparent w-full text-white outline-none text-sm"
                    />
                </div>
            </div>
            <div className="bg-[#151515] rounded-xl p-4 border border-white/10">
                <label className="text-xs text-slate-500 block mb-2 uppercase font-bold tracking-wider">IBAN / Account Number</label>
                <div className="flex items-center gap-3">
                    <Building2 className="text-slate-400" size={20} />
                    <input 
                        type="text" 
                        value={bankAccount.number}
                        onChange={(e) => setBankAccount({...bankAccount, number: e.target.value})}
                        placeholder="US12 3456..."
                        className="bg-transparent w-full text-white outline-none font-mono text-sm"
                    />
                </div>
            </div>
        </div>
        <button onClick={handleNext} disabled={!bankAccount.number} className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all">
            Continue
        </button>
        <button onClick={() => setStep(InternalWidgetStep.AMOUNT)} className="w-full text-slate-500 text-sm hover:text-white py-2">Back</button>
    </div>
  );

  const renderEmailStep = () => (
     <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
        <h3 className="text-lg font-bold text-white">Verification</h3>
        <p className="text-sm text-slate-400">We need your email to send your receipt and transaction status.</p>
        <div className="bg-[#151515] rounded-xl p-4 border border-white/10 focus-within:border-blue-500 transition-colors">
            <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="bg-transparent w-full text-white outline-none text-sm"
            />
        </div>
         <button onClick={handleNext} disabled={!email} className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all">
            Verify Email
        </button>
        <button onClick={() => setStep(mode === 'buy' ? InternalWidgetStep.ADDRESS : InternalWidgetStep.BANK)} className="w-full text-slate-500 text-sm hover:text-white py-2">Back</button>
     </div>
  );

  const renderProcessing = () => (
      <div className="flex flex-col items-center justify-center py-10 animate-in fade-in zoom-in duration-300">
          <div className="relative w-24 h-24 mb-8">
              <div className="absolute inset-0 border-4 border-blue-500/30 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                  <RefreshCw className="text-blue-500 animate-pulse" size={32} />
              </div>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Processing Transaction</h3>
          <p className="text-slate-400 text-center max-w-xs">Verifying payment and initiating blockchain transfer...</p>
      </div>
  );

  const renderSuccess = () => (
       <div className="flex flex-col items-center justify-center py-10 animate-in fade-in zoom-in duration-300">
           <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center text-black mb-8 shadow-[0_0_50px_rgba(34,197,94,0.5)]">
               <Check size={48} strokeWidth={3} />
           </div>
           <h3 className="text-2xl font-bold text-white mb-2">Success!</h3>
           <p className="text-slate-400 text-center max-w-xs mb-8">Your transaction has been processed successfully. Funds should arrive shortly.</p>
           <button onClick={handleNext} className="px-8 py-3 bg-white/10 border border-white/10 rounded-xl text-white hover:bg-white/20 transition-all">
               Start New Trade
           </button>
       </div>
  );

  return (
    <div className="w-full max-w-[440px] bg-[#0A0A0A] rounded-[32px] border border-white/10 p-6 shadow-2xl shadow-black/50 flex flex-col relative overflow-hidden font-sans">
        {/* Top Tabs */}
        <div className="flex items-center border-b border-white/5 mb-6 relative z-10">
          <button 
            onClick={() => { setMode('buy'); setStep(InternalWidgetStep.AMOUNT); }} 
            className={`flex-1 pb-4 text-sm font-bold relative transition-colors ${mode === 'buy' ? 'text-white' : 'text-slate-400 hover:text-slate-300'}`}
          >
              Buy
              {mode === 'buy' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500 shadow-[0_0_10px_#3b82f6]"></div>}
          </button>
          <button 
             onClick={() => { setMode('sell'); setStep(InternalWidgetStep.AMOUNT); }}
             className={`flex-1 pb-4 text-sm font-bold relative transition-colors ${mode === 'sell' ? 'text-white' : 'text-slate-400 hover:text-slate-300'}`}
          >
              Sell
              {mode === 'sell' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-500 shadow-[0_0_10px_#3b82f6]"></div>}
          </button>
        </div>

        {/* Main Content */}
        <div className="min-h-[420px] flex flex-col relative z-10">
            {step === InternalWidgetStep.AMOUNT && renderAmountStep()}
            {step === InternalWidgetStep.ADDRESS && renderAddressStep()}
            {step === InternalWidgetStep.BANK && renderBankStep()}
            {step === InternalWidgetStep.EMAIL && renderEmailStep()}
            {step === InternalWidgetStep.VERIFICATION && (
                <div className="text-center py-20">
                    <ScanFace size={48} className="mx-auto text-blue-500 mb-4 animate-pulse" />
                    <h3 className="text-white font-bold">Verifying Identity...</h3>
                    <p className="text-xs text-slate-500 mt-2">(Simulated)</p>
                    <button onClick={handleNext} className="mt-4 text-blue-400 text-sm hover:underline">Skip for Demo</button>
                </div>
            )}
            {(step === InternalWidgetStep.PAYMENT || step === InternalWidgetStep.DEPOSIT) && (
                 <div className="text-center py-20">
                    <CreditCard size={48} className="mx-auto text-green-500 mb-4" />
                    <h3 className="text-white font-bold">Confirm Payment</h3>
                    <p className="text-sm text-slate-400 mt-2 mb-6">Charge: {mode === 'buy' ? `${amount} ${fiatCurrency}` : `${amount} ${cryptoAsset}`}</p>
                    <button onClick={handleNext} className="w-full py-3 bg-green-600 text-white rounded-xl font-bold">Pay Now</button>
                </div>
            )}
            {step === InternalWidgetStep.PROCESSING && renderProcessing()}
            {step === InternalWidgetStep.SUCCESS && renderSuccess()}
        </div>

        {/* Security Footer */}
        <div className="mt-auto pt-6 text-center border-t border-white/5 relative z-10 space-y-3">
            <div className="flex items-center justify-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                <Lock size={10} aria-hidden="true" /> Bank Grade Security • 256-bit Encryption
            </div>
            <div className="flex items-center justify-center gap-2">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider">Powered by</span>
                <img src={logo} alt="NowRamp logo" width={22} height={16} className="h-4 opacity-60" />
            </div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 rounded-full blur-[80px] pointer-events-none"></div>
    </div>
  );
};

export default Widget;
